<template>
  <v-card flat class="pa-4">
    <v-card-title>Ventas por Producto</v-card-title>

    <!-- Chips para filtrar por organización (scroll horizontal automático) -->
    <v-chip-group
      v-model="selectedOrganization"
      class="mt-2 mb-3"
      selected-class="org-chip--selected"
      show-arrows
      center-active
      mandatory
    >
      <v-chip
        size="small"
        variant="tonal"
        :value="'todo'"
      >
        Todo
      </v-chip>

      <v-chip
        v-for="org in organizations"
        :key="org.id"
        size="small"
        variant="tonal"
        :value="String(org.id)"
      >
        {{ org.name }}
      </v-chip>
    </v-chip-group>

    <v-progress-linear v-if="loader" indeterminate color="primary" />

    <apexchart
      v-else
      type="bar"
      height="600"
      :options="chartOptions"
      :series="chartSeries"
    />
  </v-card>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue';
import { useOrganizationsStore } from '@/store/organizations';

const props = defineProps<{ data: any[], loader?: boolean }>();

// Usa string de punta a punta para evitar coerciones raras
const selectedOrganization = ref<string>('todo');

const organizationsStore = useOrganizationsStore();
const organizations = computed(() => organizationsStore.organizations);

onMounted(() => {
  if (!organizations.value.length) organizationsStore.fetchOrganizations({});
});

const filteredData = computed(() => {
  if (selectedOrganization.value === 'todo') return props.data;
  return props.data.filter(
    item => String(item.organization_id) === selectedOrganization.value
  );
});

const chartSeries = computed(() => [
  {
    name: 'Total Vendido',
    data: filteredData.value.map(item => parseFloat(item.total_vendido) || 0)
  }
]);

// Dark mode
const isDarkMode = ref(
  typeof window !== 'undefined'
    ? window.matchMedia('(prefers-color-scheme: dark)').matches
    : false
);
if (typeof window !== 'undefined') {
  window.matchMedia('(prefers-color-scheme: dark)')
    .addEventListener('change', e => (isDarkMode.value = e.matches));
}

/* =========================
   Branding por hostname
   ========================= */
const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
const isDominiomedios = hostname === 'crm.dominiomedios.com';

// Paleta por organización (Dominiomedios)
const ORGANIZATION_COLORS: Record<string, string> = {
  'Dominio Radio':   '#f40336',
  'Gamavision':      '#4d30f2',
  'Radio Naranjera': '#f24014',
  'Poster Publicidad':'#058ccd'
};
// Fallback rojo para dominiomedios
const dominiomediosFallback = '#be103b';

// Base azul para el resto de clientes
const blueBase = '#2563eb';

// Helpers para generar escala
function hexToHsl(hex: string) {
  let r = 0, g = 0, b = 0;
  const c = hex.replace('#','');
  if (c.length === 3) {
    r = parseInt(c[0] + c[0], 16);
    g = parseInt(c[1] + c[1], 16);
    b = parseInt(c[2] + c[2], 16);
  } else {
    r = parseInt(c.substring(0,2), 16);
    g = parseInt(c.substring(2,4), 16);
    b = parseInt(c.substring(4,6), 16);
  }
  r /= 255; g /= 255; b /= 255;
  const max = Math.max(r,g,b), min = Math.min(r,g,b);
  let h = 0, s = 0, l = (max + min) / 2;
  const d = max - min;
  if (d !== 0) {
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = (g - b) / d + (g < b ? 6 : 0); break;
      case g: h = (b - r) / d + 2; break;
      case b: h = (r - g) / d + 4; break;
    }
    h /= 6;
  }
  return { h, s, l };
}
function hslToHex(h: number, s: number, l: number) {
  let r: number, g: number, b: number;
  if (s === 0) {
    r = g = b = l;
  } else {
    const hue2rgb = (p: number, q: number, t: number) => {
      if (t < 0) t += 1;
      if (t > 1) t -= 1;
      if (t < 1/6) return p + (q - p) * 6 * t;
      if (t < 1/2) return q;
      if (t < 2/3) return p + (q - p) * (2/3 - t) * 6;
      return p;
    };
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    r = hue2rgb(p, q, h + 1/3);
    g = hue2rgb(p, q, h);
    b = hue2rgb(p, q, h - 1/3);
  }
  const toHex = (x: number) => Math.round(x * 255).toString(16).padStart(2, '0');
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}
function buildScale(baseHex: string, n: number) {
  const { h, s } = hexToHsl(baseHex);
  const colors: string[] = [];
  const lMin = 0.30, lMax = 0.72;
  for (let i = 0; i < n; i++) {
    const t = n === 1 ? 0.5 : i / (n - 1);
    const li = lMin + (lMax - lMin) * (1 - t);         // oscuro → claro
    const si = Math.min(1, Math.max(0.35, s * (0.85 + 0.2 * (1 - t))));
    colors.push(hslToHex(h, si, li));
  }
  return colors;
}

// Colores por barra
const chartColors = computed(() => {
  const n = Math.max(1, filteredData.value.length);
  if (isDominiomedios) {
    // Por organización con fallback rojo
    const out: string[] = [];
    for (const item of filteredData.value) {
      const orgName = item?.organizacion || '';
      out.push(ORGANIZATION_COLORS[orgName] || dominiomediosFallback);
    }
    // Si por alguna razón quedara vacío, genera una corta:
    return out.length ? out : buildScale(dominiomediosFallback, n);
  }
  // Otros clientes → escala azul
  return buildScale(blueBase, n);
});

// Color de marca para UI (chips/tooltip badge)
const brandColor = computed(() => (isDominiomedios ? dominiomediosFallback : '#4d30f2'));

// Helpers UI
function getInitials(name = '') {
  return name
    .split(' ')
    .map(w => w[0]?.toUpperCase() || '')
    .join('');
}

const chartOptions = computed(() => ({
  chart: {
    type: 'bar',
    foreColor: isDarkMode.value ? '#fff' : '#333',
    toolbar: { show: false },
  },
  plotOptions: {
    bar: {
      horizontal: true,
      distributed: true,
      borderRadius: 4,
      barHeight: '70%',
    },
  },
  dataLabels: {
    enabled: true,
    style: {
      colors: [isDarkMode.value ? '#fff' : '#333'],
      fontWeight: 'bold'
    },
    formatter: (val: any) =>
      val.toLocaleString('es-MX', { style: 'currency', currency: 'MXN' }),
  },
  xaxis: {
    categories: filteredData.value.map(item => {
      const initials = getInitials(item.organizacion || '');
      return initials ? `${initials} | ${item.description}` : item.description;
    }),
    labels: {
      show: true,
      style: {
        colors: isDarkMode.value ? '#ccc' : '#666',
        fontSize: '14px',
      },
    },
    axisBorder: { show: true },
    axisTicks: { show: true },
  },
  yaxis: {
    labels: {
      style: {
        colors: isDarkMode.value ? '#ccc' : '#666',
        fontSize: '14px',
      },
    },
  },
  tooltip: {
    theme: isDarkMode.value ? 'dark' : 'light',
    y: {
      formatter(val: number, { dataPointIndex }: any) {
        const org = filteredData.value[dataPointIndex]?.organizacion || '';
        const chip = `
          <span style="
            font-size:11px;
            background:${brandColor.value};
            color:#fff;
            border-radius:6px;
            padding:2px 7px;
            margin-left:8px;">
            ${org}
          </span>`;
        return `<div>
                  <span style="font-size:16px;">${val.toLocaleString('es-MX', { style: 'currency', currency: 'MXN' })}</span>
                  ${chip}
                </div>`;
      }
    }
  },
  grid: {
    borderColor: isDarkMode.value ? '#333' : '#eee',
    xaxis: { lines: { show: false } },
    yaxis: { lines: { show: true } },
  },
  colors: chartColors.value,   // ← paleta dinámica por hostname
  legend: { show: false },

  // Oculta labels de X sólo en mobile
  responsive: [
    {
      breakpoint: 768,
      options: {
        xaxis: {
          labels: { show: false },
          axisBorder: { show: false },
          axisTicks: { show: false },
        },
        grid: {
          xaxis: { lines: { show: false } },
        },
      },
    },
  ],
}));

const loader = computed(() => props.loader ?? false);
</script>

<style>
/* color del chip seleccionado — controlado por hostname */
.org-chip--selected {
  background: #2363eb !important; /* default (otros clientes) */
  color: white !important;
}

/* Si quieres que cambie automáticamente con Dominiomedios sin JS extra,
   puedes sobreescribir via :root desde el layout según hostname.
   O bien, deja este por defecto y ajusta con una clase alta en tu layout. */

.v-slide-group__next, .v-slide-group__prev {
  min-width: 0px!important;
}
</style>
